import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Avatar,
  Upload,
  message,
  Divider,
  Space,
} from "antd";
import {
  UserOutlined,
  UploadOutlined,
  SaveOutlined,
  PhoneOutlined,
  MailOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

const { TextArea } = Input;

const SupportProfile = ({ supportProfile, onUpdateProfile, loading }) => {
  const [form] = Form.useForm();
  const [profileImage, setProfileImage] = useState(null);

  useEffect(() => {
    if (supportProfile) {
      form.setFieldsValue({
        firstName: supportProfile.firstName || "",
        lastName: supportProfile.lastName || "",
        email: supportProfile.email || "",
        phone: supportProfile.phone || "",
        address: supportProfile.address || "",
        department: supportProfile.department || "",
        employeeId: supportProfile.employeeId || "",
        emergencyContact: supportProfile.emergencyContact || "",
      });
    }
  }, [supportProfile, form]);

  const handleSubmit = async (values) => {
    const result = await onUpdateProfile({
      ...values,
      profileImage: profileImage?.file,
    });

    if (result.success) {
      message.success("Profile updated successfully!");
    }
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isImage = file.type.startsWith("image/");
      if (!isImage) {
        message.error("You can only upload image files!");
        return false;
      }
      const isLt2M = file.size / 1024 / 1024 < 10;
      if (!isLt2M) {
        message.error("Image must be smaller than 10MB!");
        return false;
      }
      setProfileImage(file);
      return false;
    },
    showUploadList: false,
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Profile Header */}
      <Card>
        <div className="flex items-center space-x-4">
          <Avatar
            size={80}
            icon={<UserOutlined />}
            src={supportProfile?.profileImage || profileImage?.thumbUrl}
          />
          <div className="flex-1">
            <h2 className="text-2xl font-bold">
              {supportProfile?.firstName} {supportProfile?.lastName}
            </h2>
            <p className="text-gray-600">{supportProfile?.email}</p>
            <p className="text-sm text-gray-500">
              Employee ID: {supportProfile?.employeeId} | Department:{" "}
              {supportProfile?.department}
            </p>
          </div>
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>Change Photo</Button>
          </Upload>
        </div>
      </Card>

      {/* Profile Form */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="space-y-6"
      >
        {/* Personal Information */}
        <Card title="Personal Information" bordered={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[
                { required: true, message: "Please enter your first name" },
              ]}
            >
              <Input placeholder="Enter first name" />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[
                { required: true, message: "Please enter your last name" },
              ]}
            >
              <Input placeholder="Enter last name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: "Please enter your email" },
                { type: "email", message: "Please enter a valid email" },
              ]}
            >
              <Input placeholder="Enter email" prefix={<MailOutlined />} />
            </Form.Item>

            <Form.Item
              label="Phone Number"
              name="phone"
              rules={[
                { required: true, message: "Please enter your phone number" },
              ]}
            >
              <Input
                type="number"
                inputMode="numeric"
                placeholder="Enter phone number"
                prefix={<PhoneOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Address"
              name="address"
              className="md:col-span-2"
              rules={[{ required: true, message: "Please enter your address" }]}
            >
              <TextArea
                placeholder="Enter your full address"
                rows={3}
                prefix={<EnvironmentOutlined />}
              />
            </Form.Item>

            <Form.Item
              label="Emergency Contact"
              name="emergencyContact"
              rules={[
                { required: true, message: "Please enter emergency contact" },
              ]}
            >
              <Input
                placeholder="Emergency contact phone number"
                type="number"
                inputMode="numeric"
                prefix={<PhoneOutlined />}
              />
            </Form.Item>
          </div>
        </Card>

        {/* Work Information */}
        <Card title="Work Information" bordered={false}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-6">
            <Form.Item
              label="Department"
              name="department"
              rules={[{ required: true, message: "Please enter department" }]}
            >
              <Input placeholder="e.g., Customer Support, Operations" />
            </Form.Item>

            <Form.Item
              label="Employee ID"
              name="employeeId"
              rules={[{ required: true, message: "Please enter employee ID" }]}
            >
              <Input placeholder="Enter employee ID" />
            </Form.Item>
          </div>
        </Card>

        {/* Submit Button */}
        <Form.Item>
          <Space>
            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="large"
            >
              Update Profile
            </Button>
            <Button onClick={() => form.resetFields()} size="large">
              Reset
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SupportProfile;
